'use client'

import Script from 'next/script'
import { logger } from '@/utils/logger'
import { ScriptType } from '@/config/types'
import { useEffect, useRef, useCallback, useState } from 'react'

interface OptimizedScriptLoaderProps {
  scripts: ScriptType[]
  maxRetries?: number
  retryDelay?: number
}

const OptimizedScriptLoader = ({ scripts, maxRetries = 2, retryDelay = 5000 }: OptimizedScriptLoaderProps) => {
  const retryAttempts = useRef<Map<string, number>>(new Map())
  const cleanupRef = useRef<(() => void)[]>([])
  const [abandonedScriptIds, setAbandonedScriptIds] = useState<ReadonlySet<string>>(() => new Set())
  const [scriptVersions, setScriptVersions] = useState<Record<string, number>>({})

  const retryScriptLoad = useCallback(
    (scriptId: string) => {
      const attempts = retryAttempts.current.get(scriptId) || 0

      if (attempts < maxRetries) {
        const timeoutId = setTimeout(() => {
          retryAttempts.current.set(scriptId, attempts + 1)
          setScriptVersions((versions) => ({
            ...versions,
            [scriptId]: (versions[scriptId] ?? 0) + 1,
          }))
          logger.info('Retrying script load', {
            scriptId,
            attempt: attempts + 1,
            maxRetries,
            delay: retryDelay,
          })
        }, retryDelay)

        cleanupRef.current.push(() => clearTimeout(timeoutId))
      } else {
        setAbandonedScriptIds((prev) => new Set(prev).add(scriptId))
        logger.error('Script failed to load after max retries', {
          scriptId,
          maxRetries,
          totalAttempts: attempts,
        })
      }
    },
    [maxRetries, retryDelay],
  )

  const handleScriptError = useCallback(
    (scriptId: string, error: Error | Event) => {
      logger.error('Script loading failed', {
        scriptId,
        userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : 'server',
        error: error instanceof Error ? error.message : 'Unknown error',
      })

      const isCriticalScript = scriptId.includes('hubspot') || scriptId.includes('analytics')

      if (isCriticalScript) {
        retryScriptLoad(scriptId)
      }
    },
    [retryScriptLoad],
  )

  const handleScriptLoad = useCallback((scriptId: string, onLoad?: () => void) => {
    return () => {
      retryAttempts.current.delete(scriptId)

      logger.info('Script loaded successfully', {
        scriptId,
        loadTime: Date.now(),
      })

      try {
        onLoad?.()
      } catch (error) {
        logger.error('Script onLoad execution failed', {
          scriptId,
          error: error instanceof Error ? error.message : 'Unknown error',
          stack: error instanceof Error ? error.stack : undefined,
        })
      }
    }
  }, [])

  useEffect(() => {
    return () => {
      cleanupRef.current.forEach((cleanup) => cleanup())
      cleanupRef.current = []
    }
  }, [])

  return (
    <>
      {scripts.map((script) => {
        if (abandonedScriptIds.has(script.id)) return null

        const scriptKey = `${script.id}-${scriptVersions[script.id] ?? 0}`

        if (script.inlineScript) {
          return (
            <Script
              key={scriptKey}
              id={script.id}
              strategy={script.strategy}
              dangerouslySetInnerHTML={{
                __html: script.inlineScript,
              }}
              {...script.attributes}
            />
          )
        }

        return (
          <Script
            key={scriptKey}
            id={script.id}
            src={script.src}
            strategy={script.strategy}
            onLoad={handleScriptLoad(script.id, script.onLoad)}
            onError={(error) => handleScriptError(script.id, error)}
            {...script.attributes}
          />
        )
      })}
    </>
  )
}

export default OptimizedScriptLoader
